/**
 * The build document — the contract every other part of this project speaks.
 *
 * Three things will produce a `BuildDoc`, and they must all produce the *same* shape:
 *   1. a person authoring a character by hand (today),
 *   2. the app's editor UI (phase 1+),
 *   3. the companion Forge mod's character dump (after phase 3).
 *
 * That last one is why this package exists now rather than alongside the mod. The mod is
 * deferred, but the format it will emit is not — defining it up front means nothing
 * transcribed by hand today has to be re-entered later.
 *
 * ## Design rule: items are *constructed*, never a bag of finished stats
 *
 * An `Item` records the same fields Mine and Slash itself stores (`GearItemData`): a base,
 * a rarity, a level, and per-affix `{ id, tier, rollPercent }`. It never records "this item
 * gives +47 armor". The engine derives that. Recording outcomes instead of inputs would
 * make it impossible to tell a legal item from an impossible one, and the whole point of
 * hand-authored characters is that they be *recreatable* — faithful enough that a mismatch
 * against the game means the engine is wrong, not that the item was fictional.
 *
 * Every section below is optional. Phases add support incrementally, and the validator
 * checks only what is present, so a level-1 tree-only build and a fully geared one are both
 * valid documents at `schemaVersion: 1`.
 */
/** Build document format version. Bump only on an incompatible shape change. */
export const BUILD_DOC_VERSION = 1;
/** Which of the three buff slots a food occupies. `PlayerBuffData.Type`, lowercased. */
export const FOOD_BUFF_SLOTS = ["meal", "fish", "potion"];
/** Food buffs default to on. Always read `enabled` through this. */
export function isFoodBuffEnabled(buff) {
    return buff.enabled ?? true;
}
/** Auras default to on. Always read `enabled` through this. */
export function isAuraEnabled(aura) {
    return aura.enabled ?? true;
}
/**
 * `GemInventoryHelper.MAX_SKILL_GEMS` — how many Skills a character can have active at once.
 *
 *     public static int MAX_SKILL_GEMS = 8;
 *     public static int SUPPORT_GEMS_PER_SKILL = 5;
 *     public static int TOTAL_SLOTS = MAX_SKILL_GEMS * (1 + SUPPORT_GEMS_PER_SKILL);
 *
 * It is the size of the skill-gem inventory, and `getHotbarGem(i)` indexes straight into it, so
 * it is also the length of the hotbar. A ninth Skill is not a weaker Skill — there is nowhere
 * to put it.
 *
 * A document may still *hold* more than eight, the same way it holds a disabled Skill: keeping
 * the setup you are comparing against is the point of `enabled`. What it may not do is have
 * more than eight of them on at once.
 */
export const MAX_ACTIVE_SKILLS = 8;
/** Skills default to on. Always read `enabled` through this. */
export function isSkillEnabled(skill) {
    return skill.enabled ?? true;
}
/** How many of a bar's Skills are on — what {@link MAX_ACTIVE_SKILLS} bounds. */
export function activeSkillCount(skills) {
    return (skills ?? []).filter(isSkillEnabled).length;
}
/**
 * A skill's support gems as `{ id, rollPercent }`, whichever way the document spells them.
 *
 * Always read `supports` through this. A bare string entry has no roll of its own and falls
 * back to the Skill's `gemPercent`, which is what the engine did for every support gem before
 * the per-gem roll existed; `undefined` still means "nothing recorded", so the collector can
 * tell that apart from a real 0% and warn.
 */
export function supportLinks(skill) {
    return (skill.supports ?? []).map((link) => {
        const id = typeof link === "string" ? link : link.id;
        const roll = typeof link === "string" ? skill.gemPercent : link.rollPercent ?? skill.gemPercent;
        const rarity = typeof link === "string" ? undefined : link.rarity;
        const enabled = typeof link === "string" ? undefined : link.enabled;
        // Keys are omitted rather than set to `undefined`, so the result is a `SupportLink` that can
        // be written straight back into the document without leaving dead fields behind.
        const out = { id };
        if (roll !== undefined)
            out.rollPercent = roll;
        if (rarity !== undefined)
            out.rarity = rarity;
        if (enabled !== undefined)
            out.enabled = enabled;
        return out;
    });
}
/** Support links default to on. Always read `enabled` through this. */
export function isSupportEnabled(link) {
    return link.enabled ?? true;
}
/**
 * The links that actually reach the spell — {@link supportLinks} with the switched-off ones
 * dropped.
 *
 * Every consumer that asks "what is socketed here" wants this one, and every consumer that
 * *renders* the sockets wants `supportLinks`. Keeping them as two functions is what stops a
 * disabled gem from being drawn as absent or summed as present.
 */
export function activeSupportLinks(skill) {
    return supportLinks(skill).filter(isSupportEnabled);
}
/** An empty document at the current version — the starting point for hand-authoring. */
export function emptyBuild(level = 1) {
    return { schemaVersion: BUILD_DOC_VERSION, character: { level } };
}
/** The tree keys of a `BuildDoc`, paired with the registry id each one allocates into. */
export const TREE_KEYS = {
    talents: "talents",
    ascendancy: "ascendancy",
    atlas: "atlas_passives",
};
//# sourceMappingURL=build-doc.js.map